/**
 * Booking detail data layer. Same pattern as `useServiceDetail` — one
 * react-query cache per booking id, shared across the side panel + the
 * inline expanded-row Custom Fields strip on the Bookings list.
 */
import { useQuery } from "@tanstack/react-query";
import type { components } from "@/api/booqly-api";
import { api, unwrap } from "./api";
import { useAuthStore } from "./auth";
import type { CustomFieldValue } from "./service-detail";

// `customFieldValues` was added to BookingDetail by the per-booking custom
// field values backend change but the OpenAPI types haven't been
// regenerated yet. Augment the typed shape locally so callers see it.
export type BookingDetail = components["schemas"]["BookingDetail"] & {
  customFieldValues?: CustomFieldValue[];
  // `preferredDate` (ISO yyyy-MM-dd) — the booking-date custom field's value,
  // surfaced by the API but not yet in the regenerated OpenAPI types.
  preferredDate?: string | null;
};

export function useBookingDetail(id: string | null | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["booking", id],
    enabled: !!accessToken && !!id,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/bookings/{id}", {
          params: { path: { id: id! } },
        }),
      ) as BookingDetail,
  });
}
